import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppStateContext, useAppActions } from '../context/AppContext';
import { headerTranslations } from '../locales/header';
import HeaderAccessibilityMenu from './HeaderAccessibilityMenu';
import NavigationLinks from './NavigationLinks';
import ThemeToggle from './ThemeToggle';
import { BaseComponentProps } from '../types/components';

/**
 * Navigation event types
 */
type NavigationEventType = 'logout' | 'menu-open' | 'menu-close';

/**
 * PhishingHeader component props
 */
interface PhishingHeaderProps extends BaseComponentProps {
  /** Callback when navigation occurs */
  onNavigate?: ((route: string | null, eventType: NavigationEventType) => void) | null;
}

/**
 * PhishingHeader Component
 *
 * Main header component for the HelpGrandpaPhish application.
 * Provides site navigation, user authentication controls, and accessibility features.
 * Supports responsive design, internationalization, and multiple themes.
 *
 * Features:
 * - Responsive navigation with mobile hamburger menu
 * - User authentication state management (login/logout/profile)
 * - Accessibility menu integration
 * - Theme support (dark/light mode, high contrast)
 * - Internationalization (Spanish/English)
 * - Semantic HTML structure with proper ARIA attributes
 * - Keyboard navigation support
 */
const PhishingHeaderComponent: React.FC<any> = ({ 
  className = '', 
  onNavigate = null,
  'data-testid': dataTestId,
  'aria-label': ariaLabel
}) => {
  // Get app state and actions from context
  const state = useAppStateContext();
  const actions = useAppActions();
  const { textSize, language, userRole, highContrast } = state;
  const { resetState } = actions as any;

  // Get translations for current language, fallback to Spanish
  const translations = (headerTranslations as any)[language || 'es'] || (headerTranslations as any).es;

  // Navigation hook for programmatic routing
  const navigate = useNavigate();

  // State for mobile menu visibility
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  /**
   * Handles user logout with proper cleanup and navigation
   */
  const handleLogout = useCallback((): void => {
    resetState();
    navigate('/login');

    // Notify parent component if callback provided
    if (onNavigate) {
      onNavigate('/login', 'logout');
}
  }, [resetState, navigate, onNavigate]);

  /**
   * Toggles mobile menu visibility with proper ARIA state management
   */
  const handleMenuToggle = useCallback((): void => {
    setIsMenuOpen((prevState) => {
      const newState = !prevState;

      // Notify parent component if callback provided
      if (onNavigate) {
        onNavigate(null, newState ? 'menu-open' : 'menu-close');
}

      return newState;
});
  }, [onNavigate]);

  /**
   * Handles keyboard navigation for the mobile menu toggle
   */
  const handleMenuKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>): void => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleMenuToggle();
}
},
    [handleMenuToggle]
  );

  /**
   * Gets CSS classes for the header based on current theme
   */
  const getHeaderClasses = (): string => {
    const baseClasses =
      'w-full border-b shadow-sm py-2 px-4 sm:px-6 sticky top-0 z-10';

    if (highContrast) {
      return `${baseClasses} bg-high-background text-high-foreground border-high-foreground`;
}

    return `${baseClasses} bg-orange-50 border-orange-100 dark:bg-gray-800 dark:text-white dark:border-gray-700`;
  };

  return (
    <header
      className={`${getHeaderClasses()} ${className}`}
      role='banner'
      aria-label={ariaLabel || `${translations.siteHeader || 'Site header' || undefined} - HelpGrandpaPhish`}
      data-testid={dataTestId}
    >
      <div className='max-w-4xl mx-auto flex flex-col sm:flex-row justify-between items-center'>
        {/* Logo Section */}
        <div className='flex items-center mb-4 sm:mb-0 mr-4'>
          <Link
            to='/'
            aria-label={`${translations.goToHome || 'Go to home page' || undefined} - HelpGrandpaPhish`}
            className='focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 rounded flex flex-col items-center'
          >
            <img 
              src="/HGPSH Logo" 
              alt="HelpGrandpaPhish Logo" 
              className="w-10 h-10 sm:w-16 sm:h-16" 
              draggable={false}
            />
          </Link>
        </div>

        {/* Mobile Menu Toggle Button */}
        <button
          aria-label='Toggle menu'
          aria-expanded={isMenuOpen}
          aria-controls='main-navigation'
          className='sm:hidden mb-4 ml-auto focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 rounded px-2 py-1'
          onClick={handleMenuToggle}
          onKeyDown={handleMenuKeyDown}
          type='button'
        >
          <span aria-hidden='true'>&#9776;</span>
        </button>

        {/* Main Navigation */}
        <nav
          id='main-navigation'
          role='navigation'
          aria-label={translations.mainNavigation || 'Main navigation' || undefined}
          className={`${
            isMenuOpen ? 'flex' : 'hidden'
      } sm:flex flex-col sm:flex-row flex-wrap justify-center gap-2 sm:gap-4 w-full sm:w-auto`}
        >
          {/* Navigation Links Component */}
          <NavigationLinks
            {...({
              textSize,
              highContrast,
              translations,
              userRole,
              onLogout: handleLogout,
        } as any)}
          />

          {/* Accessibility Menu */}
          <HeaderAccessibilityMenu />

          {/* Theme Toggle */}
          <ThemeToggle
            showDiagnostics={process.env.NODE_ENV === 'development'}
          />
        </nav>
      </div>
    </header>
  );
};

const PhishingHeader = React.memo(PhishingHeaderComponent);

// Display name for debugging
PhishingHeader.displayName = 'PhishingHeader';

export default PhishingHeader;